import { IGNORE_BITWARDEN_AUTOFILL } from '../../../components/data-input/form-inputs/bitwarden-autofill.token';
import {
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  QueryList,
  Signal,
  signal,
  ViewChildren,
  WritableSignal,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, ParamMap } from '@angular/router';
import {
  EConfigurationChannel,
  ELicenseChannelState,
  IConfiguration, IConfigurationGroup,
  IConfigurationTableRow,
  ILicense,
} from '@web/dtos';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { map, Observable, Subscription } from 'rxjs';
import { ButtonComponent } from '../../../components/feedback/button/button.component';
import { LoadingAnimationComponent } from '../../../components/feedback/loading-animation/loading-animation.component';
import { SectionHeaderActionsComponent } from '../../../components/layout/section/section-header/section-header-actions/section-header-actions.component';
import { SectionHeaderComponent } from '../../../components/layout/section/section-header/section-header.component';
import { SectionHeadlineComponent } from '../../../components/layout/section/section-header/section-headline/section-headline.component';
import { SectionComponent } from '../../../components/layout/section/section.component';
import { SimpleTableComponent } from '../../../components/data-display/simple-table/simple-table.component';
import { ConfigurationFormsFactoryService } from '../../../factory/configuration/configuration-forms-factory.service';
import { ConfigurationStore } from '../../../services/api/configuration/configuration-store.service';
import { ConfigurationCustomInputService } from '../../../services/api/configuration/configuration-custom-input.service';
import { CmsTextProviderService } from '../../../services/cms-text-provider.service';
import { ToggleChipComponent } from '../../../components/data-display/toggle-chip/toggle-chip.component';
import { TooltipComponent } from '../../../components/feedback/tooltip/tooltip.component';
import { FormTracking } from '../form-tracking/form-tracking.component';
import {
  ConfigurationControlValue,
  IConfigurationFormData,
  IConfigurationFormDataWrapper,
  IConfigurationFormWrapper,
} from '../../../entities/configuration-entity';
import { DialogService } from '../../../services/support/dialog/dialog.service';
import { IDialogData } from '../../../components/dialogs/dialog';
import { ResetChangesDialogComponent } from '../../../components/dialogs/reset-changes-dialog/reset-changes-dialog.component';
import { ConfigurationCustomInputComponent } from './configuration-custom-input/configuration-custom-input.component';
import { ConfigurationChannelService } from '../../../services/api/configuration/configuration-channel.service';
import { SearchBarComponent } from '../../../components/data-input/form-inputs/search-bar/search-bar.component';
import { LicenseStore } from '../../../services/api/license/license-store.service';
import { ConfigurationTableRowComponent } from './configuration-table-row/configuration-table-row.component';

interface IConfigurationGroupSection {
  id: string;
  name: string;
  description: string;
  rows: IConfigurationTableRow[];
}

@Component({
  selector: 'aw-configuration-page',
  providers: [{ provide: IGNORE_BITWARDEN_AUTOFILL, useValue: true }],
  host: { class: 'block h-full min-h-0' },
  imports: [
    ButtonComponent,
    ConfigurationCustomInputComponent,
    ConfigurationTableRowComponent,
    LoadingAnimationComponent,
    NgClass,
    NgTemplateOutlet,
    ReactiveFormsModule,
    SearchBarComponent,
    SectionComponent,
    SectionHeaderActionsComponent,
    SectionHeaderComponent,
    SectionHeadlineComponent,
    SimpleTableComponent,
    ToggleChipComponent,
    TooltipComponent,
  ],
  templateUrl: './configuration-page.component.html',
})
export class ConfigurationPageComponent
  extends FormTracking<IConfigurationFormWrapper>
  implements OnDestroy
{
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly configurationStore: ConfigurationStore = inject(
    ConfigurationStore
  );
  private readonly customInputService: ConfigurationCustomInputService =
    inject(ConfigurationCustomInputService);
  private readonly configurationFactory: ConfigurationFormsFactoryService =
    inject(ConfigurationFormsFactoryService);
  private readonly configurationChannelService: ConfigurationChannelService =
    inject(ConfigurationChannelService);
  private readonly licenseStore: LicenseStore = inject(LicenseStore);
  private readonly dialogService: DialogService = inject(DialogService);
  private readonly subscription: Subscription = new Subscription();

  protected readonly cmsTextProvider: CmsTextProviderService = inject(
    CmsTextProviderService
  );
  protected readonly configurationChannel: typeof EConfigurationChannel =
    EConfigurationChannel;
  protected readonly searchControl: FormControl<string> = new FormControl('', {
    nonNullable: true,
  });
  protected readonly searchQuery: WritableSignal<string> = signal('');
  private readonly normalizedSearchQuery: Signal<string> = computed(() =>
    this.searchQuery().trim().toLocaleLowerCase()
  );
  protected readonly showWeb: WritableSignal<boolean> = signal(true);
  protected readonly showApp: WritableSignal<boolean> = signal(true);
  private readonly activeLicenseKey: WritableSignal<string> = signal('');
  private readonly activeLicenseChannels: Signal<EConfigurationChannel[]> =
    computed((): EConfigurationChannel[] => {
      const license: ILicense | undefined = this.licenseStore
        .licenses()
        .find(
          (candidate: ILicense): boolean =>
            candidate.key === this.activeLicenseKey()
        );

      if (!license) {
        return [];
      }

      return [
        ...(license.webState === ELicenseChannelState.Active
          ? [EConfigurationChannel.Web]
          : []),
        ...(license.appState === ELicenseChannelState.Active
          ? [EConfigurationChannel.App]
          : []),
      ];
    });
  protected readonly hasSingleActiveLicenseChannel: Signal<boolean> = computed(
    (): boolean => this.activeLicenseChannels().length === 1
  );
  protected readonly isLoading: Signal<boolean> = computed(
    (): boolean =>
      this.licenseStore.isLoading() ||
      this.configurationStore.isLicenseConfigurationLoading() ||
      (this.configurationStore
        .licenseConfiguration()
        .some((setting: IConfiguration): boolean =>
          this.customInputService.isSalutationsSetting(setting)
        ) && this.customInputService.isLoading())
  );
  protected readonly configurationGroups: WritableSignal<
    IConfigurationGroupSection[]
  > = signal([]);
  protected readonly operatorMappingsSetting: WritableSignal<
    IConfiguration | undefined
  > = signal(undefined);
  protected readonly licenseLabel: WritableSignal<string> = signal('');
  protected readonly mergeMenuSettingKey: WritableSignal<
    string | undefined
  > = signal(undefined);

  public configurationForm: FormGroup<IConfigurationFormWrapper> =
    this.configurationFactory.buildForm([]);

  private readonly configurationControls: Map<
    string,
    FormControl<ConfigurationControlValue>
  > = new Map();
  private readonly splitSettingsSnapshots: Map<string, IConfiguration[]> =
    new Map();
  private snapshotConfiguration: IConfiguration[] | undefined;

  @ViewChildren(SectionComponent)
  private readonly configurationSections!: QueryList<SectionComponent>;

  constructor() {
    super();
    this.subscription.add(
      this.searchControl.valueChanges.subscribe((query: string): void => {
        this.searchQuery.set(query);
      })
    );

    effect((): void => {
      this.normalizedSearchQuery();
      this.configurationGroups();
      this.expandMatchingConfigurationGroups();
    });

    effect((): void => {
      const activeChannels: EConfigurationChannel[] =
        this.activeLicenseChannels();
      if (activeChannels.length === 0) {
        return;
      }

      this.showWeb.set(activeChannels.includes(EConfigurationChannel.Web));
      this.showApp.set(activeChannels.includes(EConfigurationChannel.App));
    });

    effect((): void => {
      const loadedConfiguration: IConfiguration[] =
        this.configurationStore.licenseConfiguration();

      if (loadedConfiguration !== this.snapshotConfiguration) {
        this.splitSettingsSnapshots.clear();
        this.snapshotConfiguration = loadedConfiguration;
      }

      if (loadedConfiguration[0]?.requiredLicense.label) {
        this.licenseLabel.set(loadedConfiguration[0].requiredLicense.label);
      }
      this.configurationForm =
        this.configurationFactory.buildForm(loadedConfiguration);
      this.configurationControls.clear();
      loadedConfiguration.forEach(
        (setting: IConfiguration, index: number): void => {
          this.configurationControls.set(
            this.configurationFactory.controlKey(setting),
            this.configurationForm.controls.configurations.controls[index]
              .controls.value
          );
        }
      );
      this.customInputService.fillSalutations(
        loadedConfiguration,
        (setting: IConfiguration) => this.control(setting)
      );

      const configuration: IConfiguration[] =
        this.configurationChannelService.mergeEqualChannelSettings(
          loadedConfiguration,
          (setting: IConfiguration): ConfigurationControlValue =>
            this.control(setting).value
        );
      if (configuration !== loadedConfiguration) {
        this.rebuildConfigurationForm(configuration);
      }
      this.initFormTracking(this.configurationForm);

      this.operatorMappingsSetting.set(
        configuration.find((setting: IConfiguration): boolean =>
          this.customInputService.isOperatorMappingsSetting(setting)
        )
      );
      this.configurationGroups.set(
        this.groupConfiguration(
          configuration.filter(
            (setting: IConfiguration): boolean =>
              !this.customInputService.isOperatorMappingsSetting(setting)
          )
        )
      );
    });

    this.subscription.add(
      this.route.paramMap.subscribe((params: ParamMap) => {
        const licenseKey: string = params.get('licenseKey') ?? '';
        this.activeLicenseKey.set(licenseKey);
        this.licenseLabel.set(licenseKey);
        this.configurationStore.loadForLicense(licenseKey);
      })
    );

    window.onbeforeunload = (event: BeforeUnloadEvent): void => {
      if (this.hasConfigurationChanges(this.configurationForm)) {
        event.preventDefault();
      }
    };
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public async save(): Promise<boolean> {
    if (this.configurationForm.invalid) {
      this.configurationForm.markAllAsTouched();
      return false;
    }

    const configuration: IConfiguration[] =
      this.configurationFactory.buildEntity(this.configurationForm);
    const updatedConfiguration: IConfiguration[] =
      this.configurationChannelService.mergeEqualChannelSettings(
        configuration,
        (setting: IConfiguration): ConfigurationControlValue =>
          this.control(setting).value
      );
    if (updatedConfiguration !== configuration) {
      this.rebuildConfigurationForm(updatedConfiguration);
    }

    await this.customInputService.saveSalutations(
      updatedConfiguration,
      (setting: IConfiguration) => this.control(setting)
    );
    await this.configurationStore.saveForLicense(updatedConfiguration);
    return true;
  }

  public handleAbortChangesDialog(): Observable<boolean> {
    const dialogRef = this.dialogService.openDialog<IDialogData>(
      ResetChangesDialogComponent,
      {
        headline: this.cmsTextProvider.shared.revertChanges,
        width: '600px',
        icon: 'warning',
        iconBackgroundColor: '#eed202',
      }
    );
    dialogRef.closed.subscribe(result => {
      if (result != undefined && result.submit) {
        if (result.acceptChanges) {
          this.save();
        } else {
          this.resetFormChanges();
        }
      }
    });
    return dialogRef.closed.pipe(map(result => result?.submit ?? false));
  }

  public resetFormChanges(): void {
    this.configurationStore.reloadForLicense();
  }

  protected control(
    setting: IConfiguration
  ): FormControl<ConfigurationControlValue> {
    const control: FormControl<ConfigurationControlValue> | undefined =
      this.configurationControls.get(
        this.configurationFactory.controlKey(setting)
      );

    if (!control) {
      throw new Error(
        `No form control found for configuration '${setting.settingKey}'`
      );
    }

    return control;
  }

  protected settingId(setting: IConfiguration): string {
    return `configuration-${this.configurationFactory.controlKey(setting)}`;
  }

  protected toggleProductChannel(channel: EConfigurationChannel): void {
    if (channel === EConfigurationChannel.Web) {
      if (this.showWeb() && !this.showApp()) {
        return;
      }
      this.showWeb.update((isVisible: boolean): boolean => !isVisible);
      return;
    }

    if (this.showApp() && !this.showWeb()) {
      return;
    }
    this.showApp.update((isVisible: boolean): boolean => !isVisible);
  }

  protected readonly configurationRowKey = (
    row: IConfigurationTableRow
  ): string => row.settingKey;

  protected readonly configurationRowMatchesSearch = (
    row: IConfigurationTableRow
  ): boolean =>
    this.matchesSearchQuery(row.label, this.settingsForRow(row));

  protected configurationGroupMatchesSearch(
    group: IConfigurationGroupSection
  ): boolean {
    return group.rows.some(this.configurationRowMatchesSearch);
  }

  protected settingMatchesSearch(setting: IConfiguration): boolean {
    return this.matchesSearchQuery(setting.label, [setting]);
  }

  private expandMatchingConfigurationGroups(): void {
    if (
      this.normalizedSearchQuery().length === 0 ||
      !this.configurationSections
    ) {
      return;
    }

    const matchingGroupIds: Set<string> = new Set(
      this.configurationGroups()
        .filter((group: IConfigurationGroupSection): boolean =>
          this.configurationGroupMatchesSearch(group)
        )
        .map((group: IConfigurationGroupSection): string => group.id)
    );

    this.configurationSections.forEach(
      (section: SectionComponent): void => {
        if (matchingGroupIds.has(section.id()) && section.isCollapsed) {
          section.toggleCollapse();
        }
      }
    );
  }

  private matchesSearchQuery(
    label: string,
    settings: IConfiguration[]
  ): boolean {
    const query: string = this.normalizedSearchQuery();

    if (query.length === 0) {
      return true;
    }

    const searchableValues: string[] = settings.flatMap(
      (setting: IConfiguration): string[] =>
        this.customInputService.searchableValues(
          setting,
          this.control(setting).value
        )
    );

    return [label, ...searchableValues].some((value: string): boolean =>
      value.toLocaleLowerCase().includes(query)
    );
  }

  private settingsForRow(row: IConfigurationTableRow): IConfiguration[] {
    return [row.shared, row.web, row.app].filter(
      (setting: IConfiguration | undefined): setting is IConfiguration =>
        setting !== undefined
    );
  }

  protected areAllGroupsCollapsed(): boolean {
    return (
      !!this.configurationSections?.length &&
      this.configurationSections.toArray().every(
        (section: SectionComponent): boolean => section.isCollapsed
      )
    );
  }

  protected toggleAllGroups(): void {
    const shouldExpand: boolean = this.areAllGroupsCollapsed();

    this.configurationSections.forEach((section: SectionComponent): void => {
      if (
        section.isCollapsible() &&
        section.isCollapsed === shouldExpand
      ) {
        section.toggleCollapse();
      }
    });
  }

  public hasConfigurationChanges(
    form: FormGroup<IConfigurationFormWrapper>
  ): boolean {
    const initialFormValue: IConfigurationFormDataWrapper | undefined =
      this.initialFormValue as IConfigurationFormDataWrapper | undefined;

    if (!initialFormValue) {
      return false;
    }

    const currentFormValue: IConfigurationFormDataWrapper =
      form.getRawValue() as IConfigurationFormDataWrapper;
    return !this.configurationChannelService.haveSameFormState(
      initialFormValue.configurations,
      currentFormValue.configurations
    );
  }

  protected toggleSettingLink(row: IConfigurationTableRow): void {
    this.mergeMenuSettingKey.set(undefined);
    const configuration: IConfiguration[] =
      this.configurationFactory.buildEntity(this.configurationForm);
    const currentSettings: IConfiguration[] =
      this.configurationChannelService.settingsForKey(
        configuration,
        row.settingKey
      );

    if (row.shared) {
      const sharedSetting: IConfiguration | undefined = currentSettings[0];
      if (sharedSetting && sharedSetting.isDisabled !== true) {
        const splitSettingsSnapshot: IConfiguration[] | undefined =
          this.splitSettingsSnapshots.get(row.settingKey);

        this.replaceSettings(
          configuration,
          row.settingKey,
          splitSettingsSnapshot ??
            this.configurationChannelService.split(sharedSetting)
        );
        this.splitSettingsSnapshots.delete(row.settingKey);
      }
      return;
    }

    const webSetting: IConfiguration | undefined =
      this.configurationChannelService.settingForChannel(
        currentSettings,
        EConfigurationChannel.Web
      );
    const appSetting: IConfiguration | undefined =
      this.configurationChannelService.settingForChannel(
        currentSettings,
        EConfigurationChannel.App
      );

    if (
      webSetting &&
      appSetting &&
      !this.configurationChannelService.haveSameValue(
        this.control(webSetting).value,
        this.control(appSetting).value
      )
    ) {
      this.mergeMenuSettingKey.set(row.settingKey);
      return;
    }

    this.mergeSettings(
      configuration,
      row,
      currentSettings,
      EConfigurationChannel.Web
    );
  }

  protected selectMergeValue(
    row: IConfigurationTableRow,
    selectedChannel: EConfigurationChannel
  ): void {
    const configuration: IConfiguration[] =
      this.configurationFactory.buildEntity(this.configurationForm);
    const settings: IConfiguration[] =
      this.configurationChannelService.settingsForKey(
        configuration,
        row.settingKey
      );

    this.mergeMenuSettingKey.set(undefined);
    this.mergeSettings(configuration, row, settings, selectedChannel, true);
  }

  private mergeSettings(
    configuration: IConfiguration[],
    row: IConfigurationTableRow,
    settings: IConfiguration[],
    selectedChannel: EConfigurationChannel,
    markSalutationsDirty: boolean = false
  ): void {
    if (this.configurationChannelService.haveMixedDisabledState(settings)) {
      return;
    }

    if (settings.length > 1) {
      this.splitSettingsSnapshots.set(
        row.settingKey,
        this.deepCopyService.createDeepCopy(settings)
      );
    }

    const mergedSettings: IConfiguration[] =
      this.configurationChannelService.merge(settings, selectedChannel);
    const selectedSetting: IConfiguration | undefined =
      this.configurationChannelService.settingForChannel(
        settings,
        selectedChannel
      ) ?? settings[0];
    const selectedValue: ConfigurationControlValue | undefined = selectedSetting
      ? this.deepCopyService.createDeepCopy(this.control(selectedSetting).value)
      : undefined;

    this.replaceSettings(configuration, row.settingKey, mergedSettings);

    const mergedSetting: IConfiguration | undefined = mergedSettings[0];

    if (mergedSetting && selectedValue !== undefined) {
      this.control(mergedSetting).setValue(selectedValue);
    }
    if (
      markSalutationsDirty &&
      mergedSetting &&
      this.customInputService.isSalutationsSetting(mergedSetting)
    ) {
      this.control(mergedSetting).markAsDirty();
    }
  }

  private replaceSettings(
    configuration: IConfiguration[],
    settingKey: string,
    updatedSettings: IConfiguration[]
  ): void {
    this.rebuildConfigurationForm(
      this.configurationChannelService.replace(
        configuration,
        settingKey,
        updatedSettings
      )
    );
  }

  private rebuildConfigurationForm(configuration: IConfiguration[]): void {
    const valuesByControlKey: Map<string, ConfigurationControlValue> =
      new Map();
    const valuesBySettingKey: Map<string, ConfigurationControlValue> =
      new Map();
    const dirtySettingKeys: Set<string> = new Set();

    for (const formGroup of this.configurationForm.controls.configurations
      .controls) {
      const formData: IConfigurationFormData = formGroup.getRawValue();
      valuesByControlKey.set(
        this.configurationValueKey(formData),
        formData.value
      );
      valuesBySettingKey.set(formData.settingKey, formData.value);
      if (formGroup.controls.value.dirty) {
        dirtySettingKeys.add(formData.settingKey);
      }
    }

    this.configurationForm =
      this.configurationFactory.buildForm(configuration);
    this.configurationControls.clear();

    configuration.forEach((setting: IConfiguration, index: number): void => {
      const control: FormControl<ConfigurationControlValue> =
        this.configurationForm.controls.configurations.controls[index].controls
          .value;
      const exactValueKey: string = this.configurationValueKey(setting);
      const preservedValue: ConfigurationControlValue | undefined =
        valuesByControlKey.has(exactValueKey)
          ? valuesByControlKey.get(exactValueKey)
          : valuesBySettingKey.get(setting.settingKey);

      if (preservedValue !== undefined) {
        control.setValue(this.deepCopyService.createDeepCopy(preservedValue));
      }
      if (dirtySettingKeys.has(setting.settingKey)) {
        control.markAsDirty();
      }

      this.configurationControls.set(
        this.configurationFactory.controlKey(setting),
        control
      );
    });

    this.configurationGroups.set(
      this.groupConfiguration(
        configuration.filter(
          (setting: IConfiguration): boolean =>
            !this.customInputService.isOperatorMappingsSetting(setting)
        )
      )
    );
  }

  private configurationValueKey(
    setting: {
      id?: string | null;
      settingKey: string;
      channels: EConfigurationChannel[];
    }
  ): string {
    return (
      setting.id ??
      `${setting.settingKey}-${[...setting.channels].sort().join('-')}`
    );
  }

  private groupConfiguration(
    configuration: IConfiguration[]
  ): IConfigurationGroupSection[] {
    const rowsBySettingKey: Map<string, IConfigurationTableRow> = new Map();

    configuration.forEach((setting: IConfiguration): void => {
      const row: IConfigurationTableRow = rowsBySettingKey.get(
        setting.settingKey
      ) ?? {
        settingKey: setting.settingKey,
        label: setting.label,
        description: setting.description,
        link: setting.link,
        configurationGroup: setting.configurationGroup,
        position: setting.position,
      };
      row.link ??= setting.link;
      const isWebSetting: boolean = setting.channels.includes(
        EConfigurationChannel.Web
      );
      const isAppSetting: boolean = setting.channels.includes(
        EConfigurationChannel.App
      );

      if (isWebSetting && isAppSetting) {
        row.shared = setting;
      } else if (isWebSetting) {
        row.web = setting;
      } else if (isAppSetting) {
        row.app = setting;
      }

      rowsBySettingKey.set(setting.settingKey, row);
    });

    const sortedRows: IConfigurationTableRow[] = Array.from(
      rowsBySettingKey.values()
    ).sort(
      (first: IConfigurationTableRow, second: IConfigurationTableRow) =>
        first.position - second.position
    );

    return sortedRows.reduce(
      (
        groups: IConfigurationGroupSection[],
        row: IConfigurationTableRow
      ): IConfigurationGroupSection[] => {
        const previousGroup: IConfigurationGroupSection | undefined =
          groups[groups.length - 1];
        const configurationGroup: IConfigurationGroup | null =
          row.configurationGroup;

        if (
          previousGroup &&
          (previousGroup.id === configurationGroup?.id ||
            (!configurationGroup && previousGroup.name === ''))
        ) {
          previousGroup.rows.push(row);
          return groups;
        }

        groups.push({
          id: configurationGroup?.id ?? `general-${row.position}`,
          name: configurationGroup?.label ?? '',
          description: configurationGroup?.description ?? '',
          rows: [row],
        });
        return groups;
      },
      []
    );
  }

}
